import { motion } from 'framer-motion';
import { Brain, Shield, Zap, BarChart3, Users, Heart, Target, CheckCircle, ArrowRight, TrendingUp, Clock, AlertTriangle } from 'lucide-react';

const InsuranceFeatures = () => {
  const mainFeatures = [
    {
      category: "AI-Powered Intelligence",
      icon: Brain,
      color: "#0077B6",
      description: "Advanced machine learning algorithms that get smarter with every interaction",
      features: [
        {
          name: "Predictive Risk Modeling",
          description: "Identify high-risk members before claims spike using advanced AI algorithms",
          benefits: ["35% reduction in unexpected claims", "Early intervention programs", "Personalized risk assessments"]
        },
        {
          name: "Smart Claims Processing",
          description: "Automated claim evaluation and processing with human oversight",
          benefits: ["70% faster processing times", "99.2% accuracy rate", "Reduced manual workload"]
        },
        {
          name: "Pattern Recognition",
          description: "Identify trends and anomalies in member behavior and provider patterns",
          benefits: ["Real-time anomaly detection", "Behavioral insights", "Proactive intervention"]
        }
      ]
    },
    {
      category: "Fraud Detection & Security",
      icon: Shield,
      color: "#03045E",
      description: "Multi-layered security approach protecting your organization from fraudulent activities",
      features: [
        {
          name: "Real-time Fraud Detection",
          description: "Advanced algorithms detect suspicious claims and provider behavior instantly",
          benefits: ["98.5% fraud detection accuracy", "AED 2.1M average fraud prevented monthly", "Real-time alerts"]
        },
        {
          name: "Provider Network Monitoring",
          description: "Continuous monitoring of provider billing patterns and claim submissions",
          benefits: ["Automated provider scoring", "Suspicious pattern identification", "Network integrity maintenance"]
        },
        {
          name: "Member Verification",
          description: "Advanced identity verification and eligibility checking systems",
          benefits: ["Biometric verification", "Duplicate coverage detection", "Identity fraud prevention"]
        }
      ]
    },
    {
      category: "Operational Excellence",
      icon: Zap,
      color: "#90E0EF",
      description: "Streamlined operations that reduce costs while improving service quality",
      features: [
        {
          name: "Automated Workflows",
          description: "Intelligent automation of routine tasks and approval processes",
          benefits: ["80% reduction in manual tasks", "Consistent process execution", "Error rate minimization"]
        },
        {
          name: "Smart Approval Systems",
          description: "AI-driven pre-authorization with customizable business rules",
          benefits: ["Instant approvals for routine cases", "Complex case routing", "Approval rate optimization"]
        },
        {
          name: "Document Intelligence",
          description: "Automated document processing and data extraction",
          benefits: ["99% OCR accuracy", "Automatic data validation", "Paperless workflows"]
        }
      ]
    },
    {
      category: "Analytics & Insights",
      icon: BarChart3,
      color: "#00B4D8",
      description: "Comprehensive analytics platform providing actionable business intelligence",
      features: [
        {
          name: "Executive Dashboards",
          description: "Real-time visibility into key performance indicators and business metrics",
          benefits: ["Custom KPI tracking", "Real-time data visualization", "Executive reporting"]
        },
        {
          name: "Predictive Analytics",
          description: "Forward-looking insights to guide strategic decision making",
          benefits: ["Cost trend predictions", "Member behavior forecasting", "Market opportunity identification"]
        },
        {
          name: "Regulatory Reporting",
          description: "Automated compliance reporting and regulatory submissions",
          benefits: ["HAAD/DHA compliance", "Automated report generation", "Audit trail maintenance"]
        }
      ]
    }
  ];

  const integrationFeatures = [
    {
      name: "Seamless System Integration",
      description: "Connect with your existing infrastructure without disruption",
      icon: Target,
      benefits: ["API-first architecture", "Legacy system compatibility", "Real-time data synchronization"]
    },
    {
      name: "Member Portal Excellence",
      description: "Engaging member experience with self-service capabilities",
      icon: Users,
      benefits: ["Mobile-responsive design", "24/7 self-service options", "Personalized health insights"]
    },
    {
      name: "Provider Network Management",
      description: "Comprehensive tools for managing your provider ecosystem",
      icon: Heart,
      benefits: ["Provider performance tracking", "Automated credentialing", "Network optimization tools"]
    }
  ];

  const performanceMetrics = [
    { metric: "Claims Processing Speed", improvement: "70% faster", icon: Clock },
    { metric: "Fraud Detection Rate", improvement: "98.5% accuracy", icon: Shield },
    { metric: "Cost Reduction", improvement: "45% savings", icon: TrendingUp },
    { metric: "Member Satisfaction", improvement: "92% rating", icon: Users },
    { metric: "System Uptime", improvement: "99.9% availability", icon: CheckCircle },
    { metric: "ROI Achievement", improvement: "90 days", icon: Target }
  ];

  return (
    <section id="features" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full mb-6">
            <Zap className="w-4 h-4 text-[#03045E] mr-2" />
            <span className="text-[#03045E] font-semibold text-sm">Comprehensive Platform Features</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
            <span className="text-[#0077B6]">Advanced Technology</span> Meets Insurance Excellence
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Discover the comprehensive suite of features designed to transform your insurance operations, 
            reduce costs, and deliver exceptional member experiences.
          </p>
        </motion.div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-16">
          {performanceMetrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center bg-white rounded-2xl p-4 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
            >
              <div className="bg-[#90E0EF]/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
                <metric.icon className="w-6 h-6 text-[#0077B6]" />
              </div>
              <div className="text-lg font-bold text-[#0077B6] mb-1">{metric.improvement}</div>
              <div className="text-xs text-gray-600 leading-tight">{metric.metric}</div>
            </motion.div>
          ))}
        </div>

        {/* Main Features */}
        <div className="space-y-16 mb-16">
          {mainFeatures.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: categoryIndex * 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
            >
              {/* Category Header */}
              <div className="flex items-center space-x-4 mb-8">
                <div 
                  className="p-4 rounded-2xl"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <category.icon 
                    className="w-8 h-8" 
                    style={{ color: category.color }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-2">
                    {category.category}
                  </h3>
                  <p className="text-gray-600">{category.description}</p>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid md:grid-cols-3 gap-6">
                {category.features.map((feature, featureIndex) => (
                  <motion.div
                    key={featureIndex}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + featureIndex * 0.1 }}
                    viewport={{ once: true }}
                    className="group"
                  >
                    <div className="bg-gray-50 rounded-2xl p-6 h-full hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
                      <h4 className="text-lg font-bold text-[#03045E] mb-3 group-hover:text-[#0077B6] transition-colors">
                        {feature.name}
                      </h4>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        {feature.description}
                      </p>
                      
                      {/* Benefits */}
                      <div className="space-y-2">
                        {feature.benefits.map((benefit, benefitIndex) => (
                          <div key={benefitIndex} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Integration Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Seamless Integration & User Experience
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our platform is designed to work harmoniously with your existing systems while providing 
              exceptional experiences for all stakeholders.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {integrationFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 h-full">
                  <div className="bg-[#90E0EF]/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="w-8 h-8 text-[#0077B6]" />
                  </div>
                  
                  <h4 className="text-xl font-bold text-[#03045E] mb-4">{feature.name}</h4>
                  <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                  
                  {/* Benefits */}
                  <div className="space-y-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center justify-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Security & Compliance */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-[#CAF0F8]/30 to-[#90E0EF]/30 rounded-3xl p-8 border border-[#90E0EF]/30">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              
              {/* Left: Content */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-8 h-8 text-[#03045E]" />
                  <h3 className="text-2xl md:text-3xl font-bold text-[#03045E]">
                    Enterprise-Grade Security & Compliance
                  </h3>
                </div>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Built with security at its core, our platform ensures your data and operations 
                  meet the highest standards of protection and regulatory compliance.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    "ISO 27001 Certified",
                    "GDPR Compliant",
                    "HAAD/DHA Approved",
                    "SOC 2 Type II",
                    "End-to-end Encryption",
                    "24/7 Security Monitoring",
                    "Regular Penetration Testing",
                    "Disaster Recovery Ready"
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Right: Visual */}
              <div className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#03045E]">Security Status</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-green-600">All Systems Secure</span>
                      </div>
                    </div>
                    
                    {[
                      { name: "Data Encryption", status: 100, color: "#0077B6" },
                      { name: "Access Control", status: 100, color: "#03045E" },
                      { name: "Compliance Score", status: 98, color: "#00B4D8" },
                      { name: "Threat Detection", status: 99, color: "#90E0EF" }
                    ].map((item, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-600">{item.name}</span>
                          <span className="text-xs font-semibold" style={{ color: item.color }}>
                            {item.status}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${item.status}%` }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                            viewport={{ once: true }}
                            className="h-2 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Floating security badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  viewport={{ once: true }}
                  className="absolute -top-4 -right-4 bg-green-500 text-white p-3 rounded-full shadow-lg"
                >
                  <Shield className="w-5 h-5" />
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-[#90E0EF]/30">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Ready to see these features in action?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Experience firsthand how our comprehensive platform can transform your insurance operations 
              and deliver measurable results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => {
                  const element = document.getElementById('results');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#03045E] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#03045E]/90 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2"
              >
                <span>See Real Results</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-transparent border-2 border-[#0077B6] text-[#0077B6] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#0077B6] hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <AlertTriangle className="w-5 h-5" />
                <span>Request Demo</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsuranceFeatures;
